import { useState } from "react";
import { useRequireReauth } from "../../hooks/useRequireReauth";
import { Button } from "./Button";
import { ConfirmDialog } from "./ConfirmDialog";
import { Modal } from "./Modal";

/**
 * The standard shape for every sensitive admin action in this app: click ->
 * confirm (with a description of the blast radius) -> if not recently
 * re-authenticated, step up MFA first -> fire. Used by User Management
 * (suspend/delete/force logout/overrides), Feature Flags (global toggle),
 * System Management (maintenance mode), etc. so the "all admin actions
 * require MFA re-authentication" and "all admin actions logged" requirements
 * are enforced in one place rather than re-implemented per page.
 */
export function AdminActionButton({ label, confirmTitle, confirmBody, danger = true, onConfirm, variant = "secondary", size = "sm", disabled = false }) {
  const { requireReauth, pendingLabel, cancelReauth, startStepUp } = useRequireReauth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* disabled passes through to the trigger (#305): a required field
          that only fails at the confirm step has already made the admin
          read a blast-radius warning for an action that was never going to
          happen. */}
      <Button variant={variant} size={size} disabled={disabled} onClick={() => requireReauth(label, () => setConfirmOpen(true))}>
        {label}
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title={confirmTitle || label}
        confirmLabel={label}
        danger={danger}
        busy={busy}
      >
        {confirmBody}
      </ConfirmDialog>

      <Modal open={!!pendingLabel} onClose={cancelReauth} title="Re-authentication required" size="sm">
        <p>
          "{pendingLabel}" requires a fresh MFA verification. You'll be redirected to verify your identity, then need to
          re-click the action once you're back.
        </p>
        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
          <Button onClick={startStepUp}>Verify Identity</Button>
          <Button variant="secondary" onClick={cancelReauth}>Cancel</Button>
        </div>
      </Modal>
    </>
  );
}
